/**
 * @module nhcmap/layers/GeojsonLayer
 */
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import GeoJSON from 'ol/format/GeoJSON';
import { applyStyle } from 'ol-mapbox-style';

/**
 * @classdesc
 * Represents a GeoJSON layer
 */

class GeojsonLayer {
  /**
   * Create a GeoJSON layer
   * @param {object} layerConfig 
   * @param {string} productId 
   */
  constructor(layerConfig, productId) {
    this.layerConfig = layerConfig;
    this.productId = productId;
  }

  /**
   * Load a GeoJSON layer, dynamically applying localization if applicable.
   * @param {string} [language='en'] - The two-letter language code (e.g., 'es').
   * @returns {Promise<object>} layer
   */
  async loadLayer(language = 'en') {
    if (!this.layerConfig.url) {
      return null;
    }

    const layer = new VectorLayer({
      source: new VectorSource({
        url: this.layerConfig.url,
        format: new GeoJSON(),
      }),
      visible: this.layerConfig.visible,
      zIndex: this.layerConfig.zIndex,
    });

    await this._applyLocalizedStyle(layer, language);

    layer.set('name', this.layerConfig.id);
    return layer;
  }

  async updateLayer(layer, language) {
    const source = new VectorSource({
      url: this.layerConfig.url,
      format: new GeoJSON(),
    });
    layer.setSource(source);

    await this._applyLocalizedStyle(layer, language);
  }

  /**
   * Fetches, localizes, and applies a Mapbox style to a given layer.
   * @private
   * @param {VectorLayer} layer - The layer to apply the style to.
   * @param {string} language - The language code for localization.
   */
  async _applyLocalizedStyle(layer, language) {
    if (!this.layerConfig.styleModule) {
      return; // No style to apply
    }

    const stylePath = `/maps/${this.productId}/styles/`;
    const styleFile = `${this.layerConfig.styleModule}.json`;
    const stylePathFile = `${stylePath}${styleFile}`;

    const response = await fetch(stylePathFile);
    const styleJson = await response.json();

    for (const styleLayer of styleJson.layers) {
      if (styleLayer.layout && styleLayer.layout['text-field']) {
        const originalTextFieldExpression = styleLayer.layout['text-field'];
        let baseFieldName;
        if (Array.isArray(originalTextFieldExpression) && originalTextFieldExpression[0] === 'get') {
          baseFieldName = originalTextFieldExpression[1];
        }

        if (baseFieldName) {
          styleLayer.layout['text-field'] = [
            'coalesce',
            ['get', `${baseFieldName}_${language}`],
            ['get', `${baseFieldName}_en`],
            ['get', baseFieldName]
          ];
        }
      }
    }
    
    await applyStyle(layer, styleJson, {
      source: this.layerConfig.id,
    });
  }
}

export { GeojsonLayer };